import { useNavigate } from 'react-router-dom';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { linkWithLanguage, queryKeys } from '@splitfiesta/core';
import { useTranslation } from '@splitfiesta/i18n';
import type {
  AcceptInvitationResponseDto,
  InvitationResponseDto,
  ValidateInvitationResponseDto,
} from '@splitfiesta/shared';

import { useToast } from '@/hooks/useToast';
import { useTypedApi } from '@/hooks/useTypedApi';

import { trackInviteAccepted, trackInviteCreated } from '@/lib/analytics';

import { useAuthStore } from '@/stores/authStore';

/**
 * Hook to validate an invitation token
 */
export function useValidateInvitationQuery(token: string, enabled = true) {
  const api = useTypedApi();
  const user = useAuthStore(state => state.user);

  return useQuery({
    queryKey: queryKeys.invitationValidation(token, user?.id),
    queryFn: async () => {
      const response = await api.invitations.validate(token);
      return response.data as ValidateInvitationResponseDto;
    },
    enabled: enabled && !!token,
    // Cache for 5 minutes to avoid repeated validations
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Hook to create a new invitation
 */
export function useCreateInvitationMutation(groupId: string) {
  const api = useTypedApi();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async () => {
      const response = await api.invitations.create(groupId);
      return response.data as InvitationResponseDto;
    },
    onSuccess: data => {
      // Track analytics
      trackInviteCreated(groupId);

      // Invalidate related queries
      queryClient.invalidateQueries({
        queryKey: queryKeys.invitationsByGroup(groupId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.invitationStats(groupId),
      });

      toast({
        title: t('common:invitations.created'),
        type: 'success',
      });

      return data;
    },
    onError: error => {
      toast({
        title:
          error instanceof Error
            ? error.message
            : t('common:invitations.createFailed'),
        type: 'error',
      });
    },
  });
}

/**
 * Hook to accept an invitation
 */
export function useAcceptInvitationMutation() {
  const api = useTypedApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async ({
      token,
      claimPlaceholderId,
    }: {
      token: string;
      claimPlaceholderId?: string;
    }) => {
      const response = await api.invitations.accept(token, claimPlaceholderId);
      return response.data as AcceptInvitationResponseDto;
    },
    onSuccess: data => {
      // Track analytics
      trackInviteAccepted(data.groupId);

      // Invalidate groups list since user joined a new group
      queryClient.invalidateQueries({
        queryKey: queryKeys.groups(),
      });

      // Invalidate the validation query for this token
      queryClient.invalidateQueries({
        queryKey: queryKeys.invitationValidation(data.groupId),
      });

      toast({
        title:
          data.message ||
          t('common:invitations.joinedGroup', { groupName: data.groupName }),
        type: 'success',
      });

      // Navigate to the new group
      navigate(`/groups/${data.groupId}`);
    },
    onError: error => {
      toast({
        title:
          error instanceof Error
            ? error.message
            : t('common:invitations.acceptFailed'),
        type: 'error',
      });
    },
  });
}

/**
 * Hook for invitation link management
 */
export function useInvitationLink(
  invitation: InvitationResponseDto | null,
  groupName: string
) {
  const { toast } = useToast();
  const { t, i18n } = useTranslation();

  const getLinkWithLanguage = () =>
    invitation?.link ? linkWithLanguage(invitation.link, i18n.language) : '';

  /** What Copy puts on the clipboard: the same invite text Share sends. */
  const getShareMessage = (link = invitation?.link) =>
    link
      ? `${t('common:invitations.shareText', { groupName })}\n${linkWithLanguage(link, i18n.language)}`
      : '';

  const copyToClipboard = async () => {
    if (!invitation?.link) return;

    try {
      await navigator.clipboard.writeText(getShareMessage());
      toast({
        title: t('common:invitations.linkCopied'),
        type: 'success',
      });
      return true;
    } catch (_error) {
      toast({
        title: t('common:invitations.copyFailed'),
        type: 'error',
      });
      return false;
    }
  };

  const shareLink = async () => {
    if (!invitation?.link) return;

    // Check if Web Share API is supported
    if (typeof navigator !== 'undefined' && 'share' in navigator) {
      try {
        await navigator.share({
          title: t('common:invitations.shareTitle'),
          text: t('common:invitations.shareText', { groupName }),
          url: getLinkWithLanguage(),
        });
        return true;
      } catch (error) {
        // User cancelled or share failed
        if ((error as Error).name !== 'AbortError') {
          toast({
            title: t('common:invitations.shareFailed'),
            type: 'error',
          });
        }
        return false;
      }
    } else {
      // Fallback to copy
      return copyToClipboard();
    }
  };

  const getFullLink = () => {
    if (!invitation?.link) return '';
    return getLinkWithLanguage();
  };

  const isExpired = () => {
    if (!invitation?.expiresAt) return false;
    return new Date(invitation.expiresAt) <= new Date();
  };

  return {
    copyToClipboard,
    shareLink,
    getFullLink,
    getShareMessage,
    isExpired,
  };
}
